/* ObiHub OS — Workspace route registry (SAF, framework-bağımsız).
   Serbest URL KAYDEDİLEMEZ: yalnız burada tanımlı sabit `routeTemplate`lere ve opak parametrelere izin verilir.  Üretim rotalarına kendiliğinden BAĞLANMAZ.
   Girdi başlığın kendisini değil, çalışma zamanı başlık çözümleyicisinin KİMLİĞİNİ (`titleResolverId`) ve genel, hassas olmayan bir `defaultLabel`i taşır. */
const PARAM_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;
const KIND_PATTERN = /^[a-z][a-z0-9.]{1,31}$/;
const RESOLVER_PATTERN = /^[a-z][a-z0-9.]{1,40}$/;
const PINNED_KEY_PATTERN = /^[a-z][a-z0-9.-]{1,31}$/;
const TEMPLATE_SEGMENT = /^(?:[A-Za-z0-9_-]+|:[A-Za-z][A-Za-z0-9]*)$/;
const ENTRY_KEYS = ["kind", "routeTemplate", "paramNames", "idParam", "pinned", "closeable", "restorable", "titleResolverId", "defaultLabel", "pinnedKey"];

function deepFreeze(value) {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
}

function fail(message) {
  throw new Error(`OS route registry: ${message}`);
}

function templateParams(template) {
  return template.split("/").slice(1).filter((s) => s.startsWith(":")).map((s) => s.slice(1));
}

function assertSafeTemplate(template) {
  if (typeof template !== "string" || !template.startsWith("/") || template.startsWith("//") || template.length > 200) fail("routeTemplate geçersiz");
  const segments = template.split("/").slice(1);
  if (segments.length === 0 || segments.some((s) => !TEMPLATE_SEGMENT.test(s) || s === "." || s === "..")) fail("routeTemplate güvenli değil");
}

function normalizeEntry(raw) {
  if (!raw || typeof raw !== "object") fail("girdi nesne olmalı");
  assertSafeTemplate(raw.routeTemplate);
  if (typeof raw.kind !== "string" || !KIND_PATTERN.test(raw.kind)) fail("kind geçersiz");
  if (typeof raw.titleResolverId !== "string" || !RESOLVER_PATTERN.test(raw.titleResolverId)) fail("titleResolverId geçersiz");
  if (typeof raw.defaultLabel !== "string" || raw.defaultLabel.trim() === "" || raw.defaultLabel.length > 40) fail("defaultLabel geçersiz");
  if (typeof raw.pinned !== "boolean" || typeof raw.closeable !== "boolean" || typeof raw.restorable !== "boolean") fail("pinned/closeable/restorable boolean olmalı");
  if (!Array.isArray(raw.paramNames) || raw.paramNames.some((p) => typeof p !== "string")) fail("paramNames geçersiz");
  const fromTemplate = templateParams(raw.routeTemplate);
  const names = [...raw.paramNames];
  if (names.length !== fromTemplate.length || new Set(names).size !== names.length || names.some((n) => !fromTemplate.includes(n))) fail("parametre adları şablonla birebir uyuşmuyor");
  if (raw.pinned) {
    if (raw.closeable || raw.restorable || names.length !== 0) fail("pinned girdi kapatılamaz, geri yüklenemez ve parametresiz olmalı");
    if (typeof raw.pinnedKey !== "string" || !PINNED_KEY_PATTERN.test(raw.pinnedKey)) fail("pinnedKey geçersiz");
  } else {
    if (!raw.closeable) fail("pinned olmayan girdi closeable olmalı");
    if (names.length !== 1 || raw.idParam !== names[0]) fail("kayıt girdisi tam bir kimlik parametresi taşımalı (idParam)");
  }
  return deepFreeze({
    kind: raw.kind, routeTemplate: raw.routeTemplate, paramNames: names, idParam: raw.pinned ? null : raw.idParam, pinned: raw.pinned, closeable: raw.closeable,
    restorable: raw.restorable, titleResolverId: raw.titleResolverId, defaultLabel: raw.defaultLabel.trim(), pinnedKey: raw.pinned ? raw.pinnedKey : null,
  });
}

function createRouteRegistry(rawEntries) {
  if (!Array.isArray(rawEntries) || rawEntries.length === 0) fail("girdi listesi boş");
  const entries = rawEntries.map(normalizeEntry);
  const byKind = new Map();
  const byTemplate = new Map();
  for (const e of entries) {
    if (byKind.has(e.kind)) fail("kind tekrarı");
    if (byTemplate.has(e.routeTemplate)) fail("routeTemplate tekrarı");
    byKind.set(e.kind, e);
    byTemplate.set(e.routeTemplate, e);
  }
  return deepFreeze({
    entries,
    get: (kind) => byKind.get(kind) || null,
    getByTemplate: (template) => byTemplate.get(template) || null,
  });
}

/** Yalnız şablonun parametre adları ve opak değer deseni; aksi halde null. */
function validateParams(entry, params) {
  if (!entry || !params || typeof params !== "object" || Array.isArray(params)) return null;
  const keys = Object.keys(params);
  if (keys.length !== entry.paramNames.length || keys.some((k) => !entry.paramNames.includes(k))) return null;
  for (const k of keys) if (typeof params[k] !== "string" || !PARAM_PATTERN.test(params[k])) return null;
  return Object.freeze({ ...params });
}

function buildPath(registry, kind, params) {
  const entry = registry.get(kind);
  const valid = entry && validateParams(entry, params);
  if (!entry || !valid) throw new Error("OS route registry: buildPath için geçersiz kind/params");
  return entry.routeTemplate.split("/").map((s) => (s.startsWith(":") ? valid[s.slice(1)] : s)).join("/");
}

/** Somut bir pathname'i registry'ye karşı eşleştirir; sorgu, hash, kodlama, '..', protokol, kontrol karakteri vb. → null. */
function matchPath(registry, pathname) {
  if (typeof pathname !== "string" || pathname.length === 0 || pathname.length > 300) return null;
  if (!pathname.startsWith("/") || pathname.startsWith("//") || /[?#%\\\u0000-\u001f\u007f]|\.\./.test(pathname) || pathname.includes("://")) return null;
  const clean = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const parts = clean.split("/").slice(1);
  for (const entry of registry.entries) {
    const tpl = entry.routeTemplate.split("/").slice(1);
    if (tpl.length !== parts.length) continue;
    const params = {};
    let ok = true;
    for (let i = 0; i < tpl.length && ok; i += 1) {
      if (tpl[i].startsWith(":")) {
        if (PARAM_PATTERN.test(parts[i])) params[tpl[i].slice(1)] = parts[i];
        else ok = false;
      } else if (tpl[i] !== parts[i]) ok = false;
    }
    if (ok) return { kind: entry.kind, params: Object.freeze(params) };
  }
  return null;
}

export { PARAM_PATTERN, ENTRY_KEYS, createRouteRegistry, validateParams, buildPath, matchPath, deepFreeze };
